import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stethoscope,
    RefreshCw,
    Link2,
    Unlink,
    Search,
    AlertTriangle,
    Settings as SettingsIcon,
    Loader2,
    Ban,
    X,
    DownloadCloud,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import {
    INTEGRATION_CONNECTION,
    INTEGRATION_DOCTORS,
    INTEGRATION_EMPLOYEES,
    INTEGRATION_MAPPINGS,
    INTEGRATION_MAPPINGS_BULK,
    INTEGRATION_SYNC,
} from "../../constants/api";

const mappingStatusBadge = (s) =>
    s === "active"
        ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300"
        : s === "pending"
            ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
            : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300";

const inputCls =
    "w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary";

/** Searchable employee dropdown. Filters the already-loaded list client-side and
 *  calls onPick(employee) when one is chosen. No fuzzy auto-matching — the admin
 *  decides. */
const EmployeePicker = ({ employees, onPick, disabled }) => {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const list = !s
            ? employees
            : employees.filter((e) =>
                  [e.name, e.employee_code, e.email, e.mobile]
                      .filter(Boolean)
                      .some((v) => String(v).toLowerCase().includes(s))
              );
        return list.sort((a, b) => {
            // mapped ones go to the bottom, but otherwise preserve API order (which is sorted by name)
            if (a.mapped_doctor_id && !b.mapped_doctor_id) return 1;
            if (!a.mapped_doctor_id && b.mapped_doctor_id) return -1;
            return 0;
        });
    }, [q, employees]);

    return (
        <div className="relative w-64">
            <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                <input
                    className={cn(inputCls, "pl-8")}
                    placeholder="Search employee to map…"
                    value={q}
                    disabled={disabled}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                />
            </div>
            {open && filtered.length > 0 && (
                <div className="absolute z-20 mt-1 w-72 max-h-64 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                    {filtered.map((e) => (
                        <button
                            key={e.id}
                            type="button"
                            // onMouseDown beats the input's onBlur so the click registers
                            onMouseDown={() => {
                                setOpen(false);
                                setQ("");
                                onPick(e);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/60 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-900 dark:text-gray-100">{e.name}</span>
                                {e.mapped_doctor_id && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300">
                                        already linked
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {[e.employee_code, e.email, e.mobile].filter(Boolean).join(" · ") || "—"}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * "Integration" screen (left sidebar). Lists eMedHub doctors with active/inactive
 * flags and lets an admin map any doctor to any employee directly — no suggestions.
 * The connection (base URL, service key, IDs) is configured under Settings.
 */
const EmedHubIntegration = () => {
    const navigate = useNavigate();
    const { fetch, create, remove } = useApiMethods();
    const [loading, setLoading] = useState(true);
    const [hasConnection, setHasConnection] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [search, setSearch] = useState("");
    const [busyId, setBusyId] = useState(null); // doctor_id currently mapping/unmapping
    const [selected, setSelected] = useState(() => new Set()); // selected doctor_ids
    const [bulkBusy, setBulkBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);

    const loadConnection = async () => {
        const res = await fetch(INTEGRATION_CONNECTION, {});
        const connected = !!(res?.status === "success" && res.data);
        setHasConnection(connected);
        if (connected) setIsActive(!!res.data.is_active);
        return connected;
    };

    const loadDoctors = async (showLoader) => {
        const res = await fetch(INTEGRATION_DOCTORS, {}, showLoader ? setLoading : undefined);
        if (res?.status === "success") {
            setDoctors(res.data || []);
        } else if (res?.message) {
            ToastMsg("error", res.message);
        }
    };

    const loadEmployees = async () => {
        const res = await fetch(INTEGRATION_EMPLOYEES, {});
        if (res?.status === "success") setEmployees(res.data || []);
    };

    const init = async () => {
        setLoading(true);
        const connected = await loadConnection();
        if (connected) {
            await Promise.all([loadDoctors(false), loadEmployees()]);
        }
        setLoading(false);
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = async () => {
        await Promise.all([loadDoctors(false), loadEmployees()]);
        ToastMsg("success", "Refreshed");
    };

    const handleSync = async () => {
        setSyncing(true);
        const res = await create(INTEGRATION_SYNC, {});
        setSyncing(false);
        setConfirmSyncOpen(false);
        if (res?.status === "success") {
            const c = res.data?.counts || {};
            ToastMsg(
                "success",
                `Sync complete — ${c.created || 0} added, ${c.already_linked || 0} linked, ${c.flagged || 0} flagged`
            );
            await Promise.all([loadDoctors(false), loadEmployees()]);
        } else {
            ToastMsg("error", res?.message || "Sync failed");
        }
    };

    const handleMap = async (doctor, employee) => {
        setBusyId(doctor.doctor_id);
        const res = await create(INTEGRATION_MAPPINGS, {
            external_doctor_id: doctor.doctor_id,
            employee_id: employee.id,
            doctor_name: doctor.name,
            doctor_active: doctor.is_active,
        });
        setBusyId(null);
        if (res?.status === "success") {
            ToastMsg("success", `Mapped ${doctor.name} → ${employee.name}`);
            await Promise.all([loadDoctors(false), loadEmployees()]);
        } else {
            ToastMsg("error", res?.message || "Could not map");
        }
    };

    const handleUnmap = async (doctor) => {
        if (!doctor.mapping?.id) return;
        setBusyId(doctor.doctor_id);
        const res = await remove(`${INTEGRATION_MAPPINGS}${doctor.mapping.id}/`);
        setBusyId(null);
        if (res?.status === "success") {
            ToastMsg("success", `Unmapped ${doctor.name}`);
            await Promise.all([loadDoctors(false), loadEmployees()]);
        } else {
            ToastMsg("error", res?.message || "Could not unmap");
        }
    };

    const filteredDoctors = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return doctors;
        return doctors.filter((d) =>
            [d.name, d.speciality, d.email, d.mobile]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(s))
        );
    }, [search, doctors]);

    const mappedCount = doctors.filter((d) => d.mapping).length;

    // ---- selection ---------------------------------------------------------
    const toggleRow = (doctorId) =>
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(doctorId) ? next.delete(doctorId) : next.add(doctorId);
            return next;
        });

    const visibleIds = filteredDoctors.map((d) => d.doctor_id);
    const allVisibleSelected =
        visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    const someVisibleSelected = visibleIds.some((id) => selected.has(id));

    const toggleAll = () =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleIds.forEach((id) => next.add(id));
            }
            return next;
        });

    const clearSelection = () => setSelected(new Set());

    // How many selected rows actually have a mapping (bulk actions only touch those).
    const selectedMappedCount = doctors.filter(
        (d) => selected.has(d.doctor_id) && d.mapping
    ).length;

    const runBulk = async (action) => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        setBulkBusy(true);
        const res = await create(INTEGRATION_MAPPINGS_BULK, {
            action,
            external_doctor_ids: ids,
        });
        setBulkBusy(false);
        if (res?.status === "success") {
            ToastMsg("success", res.message || "Done");
            clearSelection();
            await Promise.all([loadDoctors(false), loadEmployees()]);
        } else {
            ToastMsg("error", res?.message || "Bulk action failed");
        }
    };

    if (loading) {
        return (
            <div className="w-full p-6">
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
                    <div className="animate-pulse space-y-3">
                        <div className="h-10 w-2/3 bg-gray-100 dark:bg-gray-700 rounded" />
                        <div className="h-40 w-full bg-gray-100 dark:bg-gray-700 rounded" />
                    </div>
                </div>
            </div>
        );
    }

    if (!hasConnection) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
                <AlertTriangle className="w-12 h-12 text-amber-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    No eMedHub connection is configured yet. Set the base URL and service
                    key under Settings &gt; Integrations first.
                </p>
                <Button
                    onClick={() => navigate("/settings/integrations")}
                    variant="brand"
                >
                    <SettingsIcon size={14} className="mr-2" /> Go to Settings
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                        <Stethoscope size={22} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                eMedHub Integration
                            </h2>
                            <span
                                className={cn(
                                    "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border",
                                    isActive
                                        ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                                        : "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
                                )}
                            >
                                <span className={cn("w-1.5 h-1.5 rounded-full", isActive ? "bg-green-500" : "bg-gray-400")} />
                                {isActive ? "Enabled" : "Disabled"}
                            </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Map each eMedHub doctor to an HRMS employee. Pick anyone you like —
                            names don't have to match. Manage the connection under{" "}
                            <button
                                className="text-sidebar-primary underline underline-offset-2"
                                onClick={() => navigate("/settings/integrations")}
                            >
                                Settings &gt; Integrations
                            </button>
                            .
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={refresh} disabled={syncing}>
                        <RefreshCw size={14} className="mr-2" /> Refresh
                    </Button>
                    <Button
                        onClick={() => setConfirmSyncOpen(true)}
                        disabled={syncing || !isActive}
                        variant="brand"
                        title={!isActive ? "Enable the connection under Settings first" : "Sync doctors as employees"}
                    >
                        {syncing ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                            <DownloadCloud size={14} className="mr-2" />
                        )}
                        {syncing ? "Syncing…" : "Sync"}
                    </Button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="relative w-72 max-w-full">
                    <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                    <input
                        className={cn(inputCls, "pl-9")}
                        placeholder="Search doctors by name / speciality…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    {mappedCount}/{doctors.length} mapped
                </div>
            </div>

            {/* Bulk action bar */}
            {selected.size > 0 && (
                <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-sidebar/30 bg-sidebar/5 dark:bg-sidebar-primary/10 px-4 py-2.5">
                    <div className="text-sm text-gray-700 dark:text-gray-200">
                        <strong>{selected.size}</strong> selected
                        <span className="text-gray-400"> · {selectedMappedCount} mapped</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {bulkBusy && <Loader2 size={16} className="animate-spin text-gray-400" />}
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={bulkBusy || selectedMappedCount === 0}
                            onClick={() => runBulk("deactivate")}
                        >
                            <Ban size={14} className="mr-1.5" /> Mark inactive
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={bulkBusy || selectedMappedCount === 0}
                            onClick={() => runBulk("unmap")}
                        >
                            <Unlink size={14} className="mr-1.5" /> Unmap
                        </Button>
                        <Button size="sm" variant="ghost" disabled={bulkBusy} onClick={clearSelection}>
                            <X size={14} className="mr-1.5" /> Clear
                        </Button>
                    </div>
                </div>
            )}

            {/* Doctor list */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
                                <th className="py-2.5 pl-4 pr-2 w-10">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 accent-sidebar-primary align-middle"
                                        checked={allVisibleSelected}
                                        ref={(el) => {
                                            if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                                        }}
                                        onChange={toggleAll}
                                        title="Select all"
                                    />
                                </th>
                                <th className="py-2.5 px-4">eMedHub Doctor</th>
                                <th className="py-2.5 px-4">Speciality</th>
                                <th className="py-2.5 px-4">Status</th>
                                <th className="py-2.5 px-4">Mapped Employee</th>
                                <th className="py-2.5 px-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDoctors.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-gray-400">
                                        No doctors found.
                                    </td>
                                </tr>
                            ) : (
                                filteredDoctors.map((d) => {
                                    const busy = busyId === d.doctor_id;
                                    const checked = selected.has(d.doctor_id);
                                    return (
                                        <tr
                                            key={d.doctor_id}
                                            className={cn(
                                                "border-b border-gray-50 dark:border-gray-800 align-middle",
                                                checked && "bg-sidebar/5 dark:bg-sidebar-primary/10"
                                            )}
                                        >
                                            <td className="py-2.5 pl-4 pr-2">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 accent-sidebar-primary align-middle"
                                                    checked={checked}
                                                    onChange={() => toggleRow(d.doctor_id)}
                                                />
                                            </td>
                                            <td className="py-2.5 px-4 text-gray-900 dark:text-gray-100 font-medium">
                                                {d.name || `Doctor #${d.doctor_id}`}
                                                <div className="text-xs font-normal text-gray-400">#{d.doctor_id}</div>
                                            </td>
                                            <td className="py-2.5 px-4 text-gray-500 dark:text-gray-400">{d.speciality || "—"}</td>
                                            <td className="py-2.5 px-4">
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border",
                                                        d.is_active
                                                            ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300"
                                                            : "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400"
                                                    )}
                                                >
                                                    <span className={cn("w-1.5 h-1.5 rounded-full", d.is_active ? "bg-green-500" : "bg-gray-400")} />
                                                    {d.is_active ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-4">
                                                {d.mapping ? (
                                                    <span className="inline-flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
                                                        <Link2 size={14} className="text-green-600" />
                                                        {d.mapping.employee_name}
                                                        {d.mapping.employee_code ? (
                                                            <span className="text-xs text-gray-400">({d.mapping.employee_code})</span>
                                                        ) : null}
                                                        {d.mapping.status ? (
                                                            <span className={cn("ml-1 px-2 py-0.5 rounded-full text-[11px] border capitalize", mappingStatusBadge(d.mapping.status))}>
                                                                {d.mapping.status === "flagged" ? "inactive" : d.mapping.status}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">Not mapped</span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    {busy && <Loader2 size={16} className="animate-spin text-gray-400" />}
                                                    {d.mapping ? (
                                                        <>
                                                            <EmployeePicker
                                                                employees={employees}
                                                                disabled={busy}
                                                                onPick={(emp) => handleMap(d, emp)}
                                                            />
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                disabled={busy}
                                                                onClick={() => handleUnmap(d)}
                                                            >
                                                                <Unlink size={14} className="mr-1.5" /> Unmap
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <EmployeePicker
                                                            employees={employees}
                                                            disabled={busy}
                                                            onPick={(emp) => handleMap(d, emp)}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmPopupModal
                open={confirmSyncOpen}
                onClose={() => setConfirmSyncOpen(false)}
                onConfirm={handleSync}
                title="Sync doctors from eMedHub?"
                description="This pulls every eMedHub doctor and adds the unmatched ones to your HRMS employee list as draft employees. Already-linked doctors are just refreshed. Are you sure you want to continue?"
                type="warning"
                confirmLabel={syncing ? "Syncing…" : "Yes, sync"}
                cancelLabel="Cancel"
                loading={syncing}
            />
        </div>
    );
};

export default EmedHubIntegration;
